import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { EASE } from "@/components/motion";

const Logo = ({ dark = false }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
    <span className="inline-block h-2.5 w-2.5 bg-gold" />
    <span
      className={`font-heading text-lg font-extrabold tracking-tight ${
        dark ? "text-white" : "text-navy"
      }`}
    >
      ICONICHO
      <span className={`ml-1.5 font-light ${dark ? "text-white/50" : "text-charcoal/40"}`}>
        LLC
      </span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 border-b bg-paper/85 backdrop-blur-xl transition-[border-color] duration-300 ${
          scrolled ? "border-charcoal/10" : "border-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Logo />

          <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-desktop">
            {NAV_LINKS.map((link) => {
              const active = !link.to.includes("#") && location.pathname === link.to;
              return (
                <Link
                  key={link.label}
                  to={link.to}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    active ? "text-navy" : "text-charcoal/60 hover:text-navy"
                  }`}
                >
                  {link.label}
                  {active && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-2 left-0 h-px w-full bg-gold"
                    />
                  )}
                </Link>
              );
            })}
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="rounded-sm bg-navy px-5 py-2.5 text-[13px] font-medium tracking-wide text-white transition-colors duration-300 hover:bg-charcoal"
            >
              Work With Iconicho
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-open-button"
            onClick={() => setOpen(true)}
            className="flex h-10 w-10 items-center justify-center text-navy lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="noise-overlay fixed inset-0 z-[60] flex flex-col bg-navy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex h-20 items-center justify-between px-6">
              <Logo dark />
              <button
                data-testid="mobile-menu-close-button"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center text-white"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.08 + i * 0.06, ease: EASE }}
                >
                  <Link
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group flex items-center justify-between border-b border-white/10 py-4"
                  >
                    <span className="font-heading text-2xl font-bold tracking-tight text-white transition-colors duration-300 group-hover:text-gold">
                      {link.label}
                    </span>
                    <ArrowUpRight className="h-5 w-5 text-white/30 transition-colors duration-300 group-hover:text-gold" />
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              className="px-8 pb-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Link
                to="/contact"
                data-testid="mobile-nav-cta-button"
                className="block rounded-sm bg-gold px-6 py-4 text-center text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-gold-dark"
              >
                Work With Iconicho
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
